import type {} from '@mui/lab/themeAugmentation';
import { createTheme, type Palette } from '@mui/material/styles';
import { type CSSProperties } from 'react';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    xlHeadline: CSSProperties;
    xlGothamHeadline: CSSProperties;
    largeHeadline: CSSProperties;
    smallHeadline: CSSProperties;
    smallerHeadline: CSSProperties;
    headline: CSSProperties;
    body: CSSProperties;
    bodyLarge: CSSProperties;
    bodyExtraLarge: CSSProperties;
    bodyLargeBold: CSSProperties;
    bodyLargeBoldSecondary: CSSProperties;
    bodySmall: CSSProperties;
    bodySmallBold: CSSProperties;

    title: React.CSSProperties;
    titleSecondary: React.CSSProperties;
    titleSmall: React.CSSProperties;
    textLargeBold: React.CSSProperties;
    textLargeBoldSecondary: React.CSSProperties;
    textLarge: React.CSSProperties;
    textMedium: React.CSSProperties;
    textMediumBold: React.CSSProperties;
    textBold: React.CSSProperties;
    text: React.CSSProperties;
    textSmall: React.CSSProperties;
    textSmallBold?: React.CSSProperties;
    textExtraSmall: React.CSSProperties;
    accent: React.CSSProperties;
    accentSmall: React.CSSProperties;
    notesSmall: React.CSSProperties;
    notesHighlightSmall: React.CSSProperties;
    breadcrumb: React.CSSProperties;
    bigBoxHeadline: React.CSSProperties;
    smallBoxHeadline: React.CSSProperties;
    boxHeadline: React.CSSProperties;
    boxText: React.CSSProperties;
    mediumHeadline: React.CSSProperties;

    badgeText: React.CSSProperties;
    badgeTextBold: React.CSSProperties;
    placementOffer: React.CSSProperties;
    footnote: React.CSSProperties;
    tag: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    xlHeadline?: CSSProperties;
    xlGothamHeadline?: CSSProperties;
    largeHeadline?: CSSProperties;
    smallHeadline?: CSSProperties;
    smallerHeadline?: CSSProperties;
    headline?: CSSProperties;
    body?: CSSProperties;
    bodyLarge?: CSSProperties;
    bodyExtraLarge?: CSSProperties;
    bodyLargeBold?: CSSProperties;
    bodyLargeBoldSecondary?: CSSProperties;
    bodySmall?: CSSProperties;
    bodySmallBold?: CSSProperties;

    title?: React.CSSProperties;
    titleSecondary?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    textLargeBold?: React.CSSProperties;
    textLargeBoldSecondary?: React.CSSProperties;
    textLarge?: React.CSSProperties;
    textMedium?: React.CSSProperties;
    textMediumBold?: React.CSSProperties;
    textBold?: React.CSSProperties;
    text?: React.CSSProperties;
    textSmall?: React.CSSProperties;
    textSmallBold?: React.CSSProperties;
    textTiny?: React.CSSProperties;
    textExtraSmall?: React.CSSProperties;
    accent?: React.CSSProperties;
    accentSmall?: React.CSSProperties;
    notesSmall: React.CSSProperties;
    notesHighlightSmall: React.CSSProperties;
    breadcrumb?: React.CSSProperties;

    bigBoxHeadline?: React.CSSProperties;
    smallBoxHeadline?: React.CSSProperties;
    boxHeadline?: React.CSSProperties;
    boxText?: React.CSSProperties;
    mediumHeadline?: React.CSSProperties;

    badgeText?: React.CSSProperties;
    badgeTextBold?: React.CSSProperties;
    placementOffer?: React.CSSProperties;
    footnote?: React.CSSProperties;
    tag?: React.CSSProperties;
  }

  interface Palette {
    blues: {
      main: string;
      medium: string;
      dark: string;
      darkest: string;
      light: string;
      lighter: string;
      lightest: string;
      extraLight: string;
    };
    greens: {
      main: string;
      extraLight: string;
      light: string;
      lighter: string;
      dark: string;
      dark2: string;
      lightest: string;
      darkest: string;
      disabled: string;
      darker: string;
    };
    yellows: {
      main: string;
      dark: string;
      darkest: string;
      light: string;
      lighter: string;
      lighter2: string;
      lightest: string;
      extraLight: string;
    };
    reds: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      lighter2: string;
      lightest: string;
    };
    neutral: {
      main: string;
      dark: string;
      darker: string;
      darkest: string;
      light: string;
      lighter: string;
      lightest: string;
      lighter2: string;
    };
    bone: {
      main: string;
    };
    limes: {
      main: string;
      tintLP: string;
      tintLP2: string;
      lightest: string;
    };
    nwGreens: {
      light: string;
      tintLP: string;
      snowyMint: string;
      mossGreen: string;
      logoLight: string;
    };
  }

  interface PaletteOptions {
    blues: {
      main: string;
      medium: string;
      dark: string;
      darkest: string;
      light: string;
      lighter: string;
      lightest: string;
      extraLight: string;
    };
    greens: {
      main: string;
      extraLight: string;
      light: string;
      lighter: string;
      dark: string;
      dark2: string;
      lightest: string;
      darker: string;
      darkest: string;
      disabled: string;
    };
    yellows: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      lighter2: string;
      lightest: string;
    };
    reds: {
      main: string;
      dark: string;
      light: string;
      lighter: string;
      lightest: string;
    };
    limes: {
      main: string;
      tintLP: string;
      tintLP2: string;
    };
    nwGreens: {
      light: string;
      tintLP: string;
      snowyMint: string;
      mossGreen: string;
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xlHeadline: true;
    xlGothamHeadline: true;
    largeHeadline: true;
    smallHeadline: true;
    smallerHeadline: true;
    headline: true;
    body: true;
    bodyLarge: true;
    bodyExtraLarge: true;
    bodyLargeBold: true;
    bodyLargeBoldSecondary: true;
    bodySmall: true;
    bodySmallBold: true;
    body1: false;
    body2: false;
    button: false;
    caption: false;
    overline: true;
    subtitle1: false;
    subtitle2: false;

    title: true;
    titleSecondary: true;
    titleSmall: true;
    textLargeBold: true;
    textLargeBoldSecondary: true;
    textLarge: true;
    textMedium: true;
    textMediumBold: true;
    textBold: true;
    text: true;
    textSmall: true;
    textSmallBold?: true;
    textExtraSmall: true;
    textTiny: true;
    accent: true;
    accentSmall: true;
    notesSmall: true;
    notesHighlightSmall: true;
    breadcrumb: true;

    bigBoxHeadline: true;
    smallBoxHeadline: true;
    boxHeadline: true;
    boxText: true;
    mediumHeadline: true;

    badgeText: true;
    badgeTextBold: true;
    placementOffer: true;
    footnote: true;
    tag: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    factChecked: true;
    factCheckedIconOnly: true;
    filterPills: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    tooltip: true;
    cdRatesTooltip: true;
  }
}

export type NWPalette = Palette;

export const palette = {
  text: {
    primary: '#000',
    secondary: '#64666A',
  },
  primary: {
    dark: '#006642',
    main: '#008254',
    light: '#40AD87',
  },
  secondary: {
    light: '#EEF7FF',
    main: '#005FB9',
  },
  grey: {
    '100': '#f7f7f7',
    '200': '#D8D9DA',
    '300': '#B1B3B5',
    '400': '#8B8C8F',
    '500': '#64666A',
    '800': '#262626',
  },
  info: {
    light: '#FFF7EA',
    dark: '#EFA92E',
    main: '#FBD083',
  },
  common: {
    white: '#ffffff',
    black: '#000',
  },
  blues: {
    main: '#005FB9',
    medium: '#0048B8',
    dark: '#02427E',
    darkest: '#02143A',
    light: '#54A0E8',
    lighter: '#9DCDFA',
    lightest: '#EEF7FF',
    extraLight: '#DEEFFF',
  },
  greens: {
    main: '#008254',
    extraLight: '#EFFCF8',
    light: '#E3FAF2',
    lighter: '#B7ECDA',
    lightest: '#E3FAF2',
    disabled: '#80C1A9',
    dark: '#006642',
    dark2: '#00682C',
    darkest: '#00482F',
    darker: '#267F4C',
  },
  reds: {
    main: '#DE2828',
    dark: '#B40000',
    light: '#FF5D5E',
    lighter: '#FF9090',
    lighter2: '#FFBFBF',
    lightest: '#FFE8E8',
  },
  yellows: {
    main: '#FFC764',
    dark: '#EFA92E',
    darkest: '#B07000',
    light: '#FBD083',
    lighter: '#FFE2AE',
    lighter2: '#FFF0D6',
    lightest: '#FFF7EA',
    extraLight: '#FFFBF4',
  },
  neutral: {
    lightest: '#F5F5F5',
    lighter: '#D8D9DA',
    lighter2: '#EBEBEC',
    light: '#B1B3B5',
    main: '#8B8C8F',
    dark: '#64666A',
    darker: '#3D4045',
    darkest: '#2A2A2A',
  },
  background: {
    default: '#fff',
  },
  bone: {
    main: '#F4EEE6',
  },
  limes: {
    main: '#E2FF39',
    tintLP: '#DDFA88',
    tintLP2: '#E8FDAC',
    lightest: '#F8FFE4',
  },
  nwGreens: {
    light: '#40AD87',
    tintLP: '#A8EA4C',
    snowyMint: '#DDFED9',
    mossGreen: '#B7E1B2',
    logoLight: '#8DC63F',
  },
} as NWPalette;

/**
 * Nerdwallet uses a 4-point spacing system compared to MUI's 8-point system.
 * This reserves the first spacing numbers to match the NW styleguide, afterwards
 * a more standard range is applied to the supplied size.
 */
type SpacingUnit = 0 | 1 | 2 | 3 | 3.5 | 4 | 5;

export const spacing = (size: SpacingUnit | number) => {
  switch (size) {
    case 0:
      return 0;
    case 1:
      return '0.25rem';
    case 2:
      return '0.5rem';
    case 3:
      return '1rem';
    case 3.5:
      return '1.5rem';
    case 4:
      return '1.75rem';
    case 5:
      return '2.75rem';
    default:
      return `${0.5 * size}rem`;
  }
};

export const iconSize = (size: string) => {
  switch (size) {
    case 'small':
      return '1rem';
    case 'medium':
      return '1.5rem';
    case 'large':
      return '2rem';
    case 'x-large':
      return '2.75rem';
    case 'xx-large':
      return '3.5rem';
    default:
      return size;
  }
};

export const GOTHAM = `var(--font-gotham), Gotham, sans-serif`;
export const GOTHAM_DISPLAY = `var(--font-gotham), Gotham Display, sans-serif`;
export const CHRONICLE_DISPLAY = `var(--font-chronicle-display), Chronicle Display, serif`;

const breakpointsValues = {
  xs: 0,
  sm: 768,
  md: 1080,
  lg: 1280,
  xl: 1440,
  xxl: 1576,
};

export const { breakpoints } = createTheme({
  breakpoints: { values: breakpointsValues },
});

export const theme = createTheme({
  palette,
  breakpoints,
  spacing,
  components: {
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        html {
          scroll-behavior: smooth;
          -webkit-font-smoothing: auto;
        }
        :root {
          --color-black: #000;
          --color-green: ${palette.primary.main};
          --color-green130: ${palette.primary.dark};
          --color-body: var(--color-black);
          --button-bg: var(--color-green);
          --form-control-text: var(--color-body);
          --form-control-trim: #b4b9c1;
        }
        body, input, select, button {
          font-size: 16px;
        }
      `,
    },

    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${palette.secondary.main}`,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        fullWidth: true,
        variant: 'contained',
      },
      styleOverrides: {
        sizeMedium: {
          fontSize: 13,
          lineHeight: 1.2,
          textAlign: 'center',
          padding: 16,
        },
        contained: {
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 'bold',
          borderRadius: 2,
          transitionDuration: '.15s',
        },
        outlined: {
          fontSize: 13,
          lineHeight: 1.2,
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 'bold',
          borderRadius: 2,
          transitionDuration: '.15s',
        },
        root: {
          variants: [
            {
              props: { variant: 'tooltip' },
              style: {
                color: 'text.primary',
                minWidth: 0,
                padding: '0 !important',
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              },
            },
            {
              props: { variant: 'cdRatesTooltip' },
              style: {
                color: 'text.primary',
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                padding: '0 !important',
                marginBottom: '3px',
                minWidth: 0,
              },
            },
            {
              props: { variant: 'tertiary' },
              style: {
                height: '44px',
                lineHeight: 1,
                letterSpacing: '0.81px',
                verticalAlign: 'top',
                background: palette.neutral.lightest,
                color: palette.primary.main,
                '&:hover': {
                  background: palette.neutral.lighter,
                },
                '&:active': {
                  background: palette.common.white,
                },
                '&.Mui-disabled': {
                  background: palette.neutral.lightest,
                  color: palette.primary.main,
                },
                '@media (hover: none)': {
                  '&:hover': {
                    background: palette.neutral.lightest,
                  },
                  '&:active': {
                    background: palette.neutral.lighter,
                  },
                },
              },
            },
          ],
        },
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'secondary',
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: palette.secondary.main,
          fontFamily: GOTHAM,
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: 'factChecked' },
              style: {
                border: '1px solid',
                width: 'fit-content',
                borderColor: palette.secondary.main,
                color: palette.secondary.main,
                fontWeight: 500,
                fontFamily: GOTHAM,
                backgroundColor: palette.blues.lightest,
                height: 'fit-content',
                '& .MuiChip-label': {
                  padding: '0 0.5rem',
                },
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                  width: '14px',
                  height: '14px',
                },
              },
            },
            {
              props: { variant: 'filterPills' },
              style: {
                color: palette.text.secondary,
                backgroundColor: '#EBEBEC',
                height: 32,
                letterSpacing: '0.03rem',
                textTransform: 'none',
                fontWeight: 500,
                fontFamily: GOTHAM,
                fontSize: 12,
                '&:focus-visible': {
                  border: `2px solid ${palette.blues.light}`,
                  outline: 'none',
                  backgroundColor: '#EBEBEC',
                },
                '& .MuiChip-deleteIcon': {
                  color: palette.text.secondary,
                  fontSize: 18,
                  marginLeft: -6,
                  marginRight: 12,
                  '&:hover': {
                    color: palette.text.secondary,
                  },
                },
              },
            },
          ],
        },
      },
    },
    MuiLoadingButton: {
      defaultProps: {
        variant: 'contained',
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        thickness: 2.5,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 16,
        },
        sizeSmall: {
          fontSize: 13,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: 'inherit',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${palette.grey[200]}`,
          borderBottom: 'none',
          '&:last-of-type': {
            borderBottom: `1px solid ${palette.grey[200]}`,
          },
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordion-root.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: spacing(3),
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': {
            outlineColor: palette.blues.light,
            outlineStyle: 'solid',
            outlineWidth: '2px',
            backgroundColor: palette.blues.lightest,
            zIndex: 1,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            fontSize: '20px',
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(-180deg)',
          },
          [breakpoints.down('sm')]: {
            '&:hover': {
              outline: 'none !important',
              backgroundColor: 'palette.blues.lightest',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-scroller': {
            [breakpoints.down('md')]: {
              overflowX: 'scroll !important',
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          fontSize: '24px',
          '&:before': {
            color: palette.common.white,
            border: `1px solid ${palette.grey['300']}`,
          },
        },
        tooltip: {
          padding: `${spacing(3)} ${spacing(3)} ${spacing(0)} ${spacing(3)}`,
          maxWidth: '36rem',
          backgroundColor: palette.common.white,
          boxShadow: '0 4px 8px 0 rgb(100 102 106 / 15%), 0 8px 16px 0 rgb(100 102 106 / 33%)',
          color: palette.text.primary,
          border: `1px solid ${palette.grey['300']}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          borderBottomColor: palette.common.black,
          fontSize: '1rem',
          paddingBottom: '8px',
        },
        root: {
          '&:first-of-type': {
            paddingLeft: 0,
          },
        },
      },
    },
  },
  typography: {
    fontFamily: GOTHAM,
    xlHeadline: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 32,
      fontWeight: 400,
      lineHeight: 1.1,
      [breakpoints.up('md')]: {
        fontSize: '3.375rem',
      },
    },
    xlGothamHeadline: {
      fontSize: 40,
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.005em',
      [breakpoints.down(768)]: {
        fontSize: 24,
        letterSpacing: '0.01em',
      },
    },
    largeHeadline: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.167,
      [breakpoints.up('md')]: {
        fontSize: '2.25rem',
      },
    },
    smallHeadline: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    smallerHeadline: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '0.75rem',
    },
    headline: {
      fontWeight: 700,
      lineHeight: 1.33,
      fontSize: '1.5rem',
    },
    h1: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 32,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 36,
      },
    },
    h2: {
      fontSize: 24,
      lineHeight: 1.3,
      fontWeight: '500',
    },
    h3: {
      fontSize: 20,
      lineHeight: 1.3,
      fontWeight: '400',
    },
    h4: {
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: '500',
    },
    h5: {
      fontFamily: CHRONICLE_DISPLAY,
    },
    h6: {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    body: {
      fontSize: 16,
      lineHeight: 1.5,
    },
    bodyLarge: {
      fontSize: 20,
      lineHeight: 1.5,
    },
    bodyExtraLarge: {
      fontSize: 24,
      lineHeight: 1.5,
    },
    bodyLargeBold: {
      fontSize: 20,
      lineHeight: 1.3,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    bodyLargeBoldSecondary: {
      fontSize: 20,
      lineHeight: '26.6px',
      fontWeight: 500,
      [breakpoints.up('md')]: {
        lineHeight: '30px',
      },
    },
    bodySmall: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    bodySmallBold: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 700,
    },

    /* Currency Type styles */
    title: {
      fontSize: 32,
      lineHeight: '111%',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 48,
        lineHeight: '53px',
      },
    },
    titleSecondary: {
      fontSize: 32,
      lineHeight: '35.52px',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('sm')]: {
        fontSize: 48,
        lineHeight: '53px',
      },
    },
    titleSmall: {
      fontSize: 24,
      lineHeight: '117%',
      fontFamily: CHRONICLE_DISPLAY,
      fontWeight: 400,
      [breakpoints.up('md')]: {
        fontSize: 36,
        lineHeight: '42px',
      },
    },
    textLargeBold: {
      fontSize: 20,
      lineHeight: '133%',
      fontWeight: 500,
      fontFamily: GOTHAM_DISPLAY,
      [breakpoints.up('md')]: {
        fontSize: 24,
        lineHeight: '32px',
      },
    },
    textLargeBoldSecondary: {
      fontSize: 20,
      lineHeight: '26.6px',
      fontWeight: 500,
      [breakpoints.up('sm')]: {
        fontSize: 24,
        lineHeight: '32px',
      },
    },
    textLarge: {
      fontSize: 18,
      lineHeight: '133%',
      fontWeight: 300,
      [breakpoints.up('md')]: {
        fontSize: 20,
        lineHeight: '32px',
      },
    },
    textMedium: {
      fontSize: 16,
      lineHeight: '24px',
      fontWeight: 300,
      [breakpoints.up('md')]: {
        fontSize: 18,
        lineHeight: '27px',
      },
    },
    textMediumBold: {
      fontSize: 16,
      lineHeight: '24px',
      fontWeight: 500,
      [breakpoints.up('md')]: {
        fontSize: 18,
        lineHeight: '27px',
      },
    },
    textBold: {
      fontSize: 16,
      lineHeight: '150%',
      fontFamily: GOTHAM_DISPLAY,
      fontWeight: 500,
    },
    text: {
      fontSize: 16,
      lineHeight: '150%',
      fontWeight: 300,
    },
    textSmall: {
      fontSize: '.813rem',
      lineHeight: 1.4,
    },
    textSmallBold: {
      fontSize: '.813rem',
      lineHeight: 1.4,
      fontFamily: GOTHAM_DISPLAY,
      fontWeight: 500,
    },
    textExtraSmall: {
      fontSize: '0.688rem',
      fontWeight: 400,
      letterSpacing: '0.04rem',
      WebkitFontSmoothing: 'auto',
    },
    textTiny: {
      fontSize: '0.625rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.005em',
    },
    accent: {
      fontSize: 14,
      lineHeight: '15px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8125px',
    },
    accentSmall: {
      fontSize: 12,
      lineHeight: '18px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8125px',
    },
    notesSmall: {
      fontSize: 12,
      lineHeight: 1.5,
      fontWeight: 325,
      letterSpacing: '0.005em',
    },
    notesHighlightSmall: {
      fontSize: 10,
      lineHeight: 1.4,
      fontWeight: 500,
      color: palette.limes.tintLP2,
      letterSpacing: '0.005em',
      textTransform: 'uppercase',
    },
    breadcrumb: {
      fontSize: 10,
      lineHeight: '24px',
      letterSpacing: '.3px',
      textTransform: 'uppercase',
      fontWeight: 700,
    },
    /* Performance Marketing - New Styles */
    boxHeadline: {
      fontSize: 20,
      lineHeight: '26.6px',
      fontWeight: 500,
      [breakpoints.up('sm')]: {
        fontSize: 24,
        lineHeight: '32px',
      },
    },
    bigBoxHeadline: {
      fontSize: 24,
      lineHeight: '32px',
      fontWeight: 500,
    },
    smallBoxHeadline: {
      fontSize: 16,
      lineHeight: '19.2px',
      fontWeight: 600,
    },
    boxText: {
      fontSize: 16,
      lineHeight: '24px',
      fontWeight: 400,
    },
    mediumHeadline: {
      fontFamily: CHRONICLE_DISPLAY,
      fontSize: 36,
      fontWeight: 400,
      lineHeight: '53px',
    },
    badgeText: {
      fontWeight: 400,
      lineHeight: 1.2,
      fontSize: '0.75rem',
    },
    badgeTextBold: {
      fontWeight: 500,
      lineHeight: 1.2,
      fontSize: '0.75rem',
    },
    placementOffer: {
      fontSize: 43,
      fontWeight: 400,
      lineHeight: '49.45px',
    },
    footnote: {
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '.3px',
    },
    tag: {
      color: 'black',
      letterSpacing: '0.04rem',
      fontSize: '0.688rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      backgroundColor: palette.grey['100'],
      borderRadius: '0.625rem',
      textWrap: 'nowrap',
    },
  },
});
